import { type Dimensions } from '@opentok/client';

export enum Vendor {
  Vonage = 'Vonage',
}

/**
 * @enum Media access errors
 */
export enum MediaAccessError {
  PermissionDenied = 'PermissionDenied',
  PermissionDeniedBySystem = 'PermissionDeniedBySystem',
  DeviceNotFound = 'DeviceNotFound',
  IncorrectConstraints = 'IncorrectConstraints',
  NoVideoAudioConstraints = 'NoVideoAudioConstraints',
  Error = 'Error',
}

export enum MediaAccessStatus {
  Allowed = 'Allowed',
  Denied = 'Denied',
  Error = 'Error',
}

export enum FacingMode {
  User = 'user',
  Environment = 'environment',
}

export enum DeviceKind {
  VideoInput = 'videoinput',
  AudioInput = 'audioinput',
  AudioOutput = 'audiooutput',
}

export enum MediaTrackKind {
  Video = 'video',
  Audio = 'audio',
}

/**
 * Event types related to media updates
 */
export enum MediaEventType {
  /**
   * New permissions assigned
   */
  MediaAccessStatusChange = 'MediaAccessStatusChange',
  /**
   * Fired when video/audio is turned on/off
   */
  MediaStatusChange = 'MediaStatusChange',
  /**
   * Fired when a device is connected or disconnected
   */
  DevicesChange = 'DevicesChange',
  /**
   * Currently selected device changed, e.g. switched to the other camera
   */
  SelectedDevicesChange = 'SelectedDevicesChange',
  VideoElementUpdated = 'VideoElementUpdated',
  TracksAdded = 'TracksAdded',
  TracksRemoved = 'TracksRemoved',
  MediaStreamAudioTrackUpdated = 'MediaStreamAudioTrackUpdated',
  MediaStreamVideoTrackUpdated = 'MediaStreamVideoTrackUpdated',
  MediaStreamScreenTrackUpdated = 'MediaStreamScreenTrackUpdated',
  MediaStreamScreenTrackStarted = 'MediaStreamScreenTrackStarted',
  MediaStreamScreenTrackEnded = 'MediaStreamScreenTrackEnded',
  MediaSourceUpdated = 'MediaSourceUpdated',
  MediaAudioFallbackEnabled = 'MediaAudioFallbackEnabled',
  MediaAudioFallbackDisabled = 'MediaAudioFallbackDisabled',
  MediaVideoDisableWarningEnabled = 'MediaVideoDisableWarningEnabled',
  MediaVideoDisableWarningDisabled = 'MediaVideoDisableWarningDisabled',
  CaptionsPublishToggled = 'CaptionsPublishToggled',
  /**
   * Fired when media permissions are denied
   * Helps detect denial in browsers that don’t update permission status reliably
   */
  MediaAccessDenied = 'MediaAccessDenied',
}

export enum MediaStorageKeys {
  SelectedDevices = 'devicesInfo', // would be better '[media]selected-devices', but it is shared with angular app
}

export type VideoQualityConstraint = {
  frameRate: number;
  height: number;
  width: number;
};

export type VideoQuality = 'ld' | 'sd' | 'hd';

export const VideoQualityProfiles: Record<
  VideoQuality,
  VideoQualityConstraint
> = {
  ld: {
    frameRate: 15,
    height: 240,
    width: 320,
  },
  sd: {
    frameRate: 30,
    height: 480,
    width: 640,
  },
  hd: {
    frameRate: 30,
    height: 720,
    width: 1280,
  },
} as const;

export const VideoQualityProfilesWide: Record<
  VideoQuality,
  VideoQualityConstraint
> = {
  ld: {
    frameRate: 15,
    height: 180,
    width: 320,
  },
  sd: {
    frameRate: 30,
    height: 360,
    width: 640,
  },
  hd: {
    frameRate: 30,
    height: 720,
    width: 1280,
  },
} as const;

export const VideoQualityDimensions: Record<VideoQuality, Dimensions> = {
  ld: {
    height: VideoQualityProfiles.ld.height,
    width: VideoQualityProfiles.ld.width,
  },
  sd: {
    height: VideoQualityProfiles.sd.height,
    width: VideoQualityProfiles.sd.width,
  },
  hd: {
    height: VideoQualityProfiles.hd.height,
    width: VideoQualityProfiles.hd.width,
  },
} as const;
